import customFieldsService from '../../../services/CustomFieldsService';
import customFieldsConstants from '../../../constants/CustomFieldsConstants';
import utilService from '../../../services/UtilService';
import sessionReader from '../../../store/readHelpers/SessionReader';
import scorableItemNumberingService from '../../../services/questionView/ScorableItemNumberingService';
import { t } from '../../../services/i18nService';

const fjordRubricsLoader = {
    addToObjectIfValue(captionProperties, customFields, key, value) {
        const result = customFieldsService.getCustomFieldValue(customFields, value);
        if (result) {
            captionProperties[key] = result;
        }
    },

    addPBValueToObjectIfValue(captionProperties, customFields, key, value) {
        const result = customFieldsService.getCustomFieldValue(customFields, value);
        if (key === 'itemRubricBlank3' && result && result.length) {
            captionProperties.itemRubricBlank3HtmlForPB = t('rubric.question.pb.reading.itemRubricBlank3');
            return captionProperties.itemRubricBlank3HtmlForPB;
        }

        return null;
    },

    addPBPagesPlaceholder(captionProperties) {
        captionProperties.startPagePlaceholder = 'pages X';
        captionProperties.endPagePlaceholder = ' and X';
    },

    addMPCProperties(captionProperties, firstResponseModel) {
        const maxChoices = firstResponseModel.maxChoices ? firstResponseModel.maxChoices : undefined;
        if (maxChoices > 0) {
            captionProperties.maxChoices = maxChoices;
            captionProperties.maxChoicesWordUpperCase = utilService.getUpperCaseWordFromNumber(maxChoices);
            captionProperties.numberOfAlternatives = firstResponseModel.alternatives.length;
            captionProperties.lastAlternativeLetter = utilService.toLatinLetter(captionProperties.numberOfAlternatives);
            captionProperties.alternativeLetterRange = this.createLetterRange(firstResponseModel.alternatives);
        }
    },

    addMatchingProperties(captionProperties, firstResponseModel) {
        const matchColumns = firstResponseModel.type === 'matchInteraction' && firstResponseModel.alternatives.length === 2
            ? firstResponseModel.alternatives[1].length
            : undefined;

        if (matchColumns > 0) {
            captionProperties.matchColumns = matchColumns;
            captionProperties.matchColumnsLetter = utilService.toLatinLetter(matchColumns);
            captionProperties.matchColumnsLetterRange = this.createLetterRange(firstResponseModel.alternatives[1]);
            const upperCaseWordFromNumber = utilService.getUpperCaseWordFromNumber(matchColumns);
            captionProperties.matchColumnsWordLowerCase = upperCaseWordFromNumber ? upperCaseWordFromNumber.toLowerCase() : undefined;
            captionProperties.matchColumnsWordUpperCase = upperCaseWordFromNumber;
        }
    },

    addGapMatchProperties(captionProperties, firstResponseModel) {
        const gapChoiceCount = firstResponseModel.type === 'gapMatchInteraction' && firstResponseModel.gapChoiceModels.length
            ? firstResponseModel.gapChoiceModels.length
            : undefined;

        if (gapChoiceCount > 0) {
            captionProperties.lastGapChoiceLetter = utilService.toLatinLetter(gapChoiceCount);
            captionProperties.lastGapChoiceRomanLetter = utilService.toRomanNumeral(firstResponseModel.gapChoiceModels.length);
        }
    },

    createLetterRange(array) {
        let letterRange = '';
        if (array.length > 4) {
            return `<strong>A–${utilService.toLatinLetter(array.length)}</strong>`;
        }

        array.forEach((item, index) => {
            if (index === array.length - 1) {
                letterRange += ` or <strong>${utilService.toLatinLetter(index + 1)}</strong>`;
            } else if (index === 0) {
                letterRange += `<strong>${utilService.toLatinLetter(index + 1)}</strong>`;
            } else {
                letterRange += `, <strong>${utilService.toLatinLetter(index + 1)}</strong>`;
            }
        });
        return letterRange;
    },

    getQuestionRubricCaptionProperties(question) {
        const { contentRevisionId, customFields } = question;
        const scorableItemsInQuestion = scorableItemNumberingService.getScorableItemsInQuestion(contentRevisionId);
        const captionProperties = {
            interpolation: { escapeValue: false },
        };

        if (Array.isArray(scorableItemsInQuestion) && scorableItemsInQuestion.length) {
            const firstResponseModel =
                question.responseModels && question.responseModels.length && question.responseModels[0] ? question.responseModels[0] : undefined;
            captionProperties.startNumber = scorableItemsInQuestion[0].orderNumber;
            captionProperties.endNumber = scorableItemsInQuestion[scorableItemsInQuestion.length - 1].orderNumber;
            captionProperties.startLetter = utilService.toLatinLetter(1);
            captionProperties.endLetter = utilService.toLatinLetter(scorableItemsInQuestion.length);
            captionProperties.letterRange = this.createLetterRange(scorableItemsInQuestion);
            captionProperties.questionsCount = scorableItemsInQuestion.length;
            const upperCaseWordQuestionCount = utilService.getUpperCaseWordFromNumber(captionProperties.questionsCount);
            captionProperties.questionsCountWordUpperCase = upperCaseWordQuestionCount;
            captionProperties.questionsCountWordLowerCase = upperCaseWordQuestionCount?.toLowerCase();

            if (firstResponseModel) {
                this.addMPCProperties(captionProperties, firstResponseModel);
                this.addMatchingProperties(captionProperties, firstResponseModel);
                this.addGapMatchProperties(captionProperties, firstResponseModel);
            }

            const itemSubType = utilService.toCamelCaseRemoveNonAlphaNumeric(
                customFieldsService.getCustomFieldValue(customFields, customFieldsConstants.ITEM_SUB_TYPE)
            );
            captionProperties.itemType = itemSubType || utilService.toCamelCaseRemoveNonAlphaNumeric(
                customFieldsService.getCustomFieldValue(customFields, customFieldsConstants.ITEM_TYPE)
            );

            this.addToObjectIfValue(captionProperties, customFields, 'itemRubricBlank1', customFieldsConstants.ITEM_RUBRIC_BLANK_1);
            this.addToObjectIfValue(captionProperties, customFields, 'itemRubricBlank2', customFieldsConstants.ITEM_RUBRIC_BLANK_2);
            this.addToObjectIfValue(captionProperties, customFields, 'itemRubricBlank3', customFieldsConstants.ITEM_RUBRIC_BLANK_3);
            this.addPBValueToObjectIfValue(captionProperties, customFields, 'itemRubricBlank3', customFieldsConstants.ITEM_RUBRIC_BLANK_3);
            this.addToObjectIfValue(captionProperties, customFields, 'itemRubricIntroduction', customFieldsConstants.ITEM_RUBRIC_INTRODUCTION);
            captionProperties.testingFocusLevel1 = utilService.toCamelCaseRemoveNonAlphaNumeric(
                customFieldsService.getCustomFieldValue(customFields, customFieldsConstants.TESTING_FOCUS_LEVEL_1_FJORD)
            );
            this.addPBPagesPlaceholder(captionProperties);
        }
        return captionProperties;
    },

    returnItemRubricVariantAsCaption(question) {
        const { customFields } = question;
        const itemRubricVariant = utilService.toCamelCaseRemoveNonAlphaNumeric(
            customFieldsService.getCustomFieldValue(customFields, customFieldsConstants.ITEM_RUBRIC_VARIANT)
        );
        return itemRubricVariant ? t(`rubric.question.rubricVariant.${itemRubricVariant}`) : undefined;
    },

    addMultiAnswerProperties(question, choiceModel, captionProperties) {
        // const { choiceModel, question } = this.props;
        let startNumber = captionProperties.startNumber;
        let endNumber = captionProperties.endNumber;

        question.responseModels.find((model, index) => {
            if (index > 0) {
                startNumber = endNumber + 1;
            }
            endNumber = startNumber + (choiceModel.maxChoices - 1);
            return model.identifier === choiceModel.identifier;
        });

        captionProperties.startNumber = startNumber;
        captionProperties.endNumber = endNumber;
    },

    getRubricsContent(question, choiceModel) {
        const captionProperties = this.getQuestionRubricCaptionProperties(question);
        captionProperties.itemRubricVariant = this.returnItemRubricVariantAsCaption(question);

        const isPaperBased = sessionReader.isPrintStylePaperBased();
        const captionPrefix = isPaperBased ? 'pb' : 'cd';
        if (!captionProperties.itemType) {
            return null;
        }

        const singularSuffix = customFieldsService.getSuffix(captionProperties, isPaperBased);

        if (isPaperBased && captionProperties.itemType === customFieldsConstants.itemTypes.MC_QUESTIONS_MULTI_ANSWER) {
            if (!choiceModel) {
                return null;
            }
            this.addMultiAnswerProperties(question, choiceModel, captionProperties);
        }

        return t(
            `rubric.question.${captionPrefix}.${captionProperties.testingFocusLevel1}.${captionProperties.itemType}${singularSuffix}`,
            captionProperties
        );
    },
};

export default fjordRubricsLoader;
